import {Client, ClientUser, Item, Workspace} from '../../../API';
import {allMockClientUsers, mockClient, mockWorkspace, mockRoles, mockUserRoles} from './fixtures';

const STORAGE_KEY = 'lgc-mock-db-v2';

export interface MockDb {
    client: Client;
    clientUsers: ClientUser[];
    workspaces: Workspace[];
    items: Item[];
    wsUsers: any[];           // WorkspaceUser records
    taskListUnits: any[];     // TaskListUnit records (taskLists embedded as JSON)
    tasks: any[];             // Task records
    taskReports: any[];       // TaskReport records (one per task per logical day)
    // ── Reliability + Audits ("Фича аудит") ──────────────────────────────────
    locations: any[];         // Location records (audit subjects + issue place tags)
    issues: any[];            // Issue records (overdue / cant_complete / manual_report / audit_finding)
    auditTemplates: any[];    // AuditTemplate records
    auditQuestions: any[];    // AuditQuestion records (options/reference are nested objects)
    auditRuns: any[];         // AuditRun records (server-owned score/percentage/passed)
    auditAnswers: any[];      // AuditAnswer records (one per run per question)
    auditAssignments: any[];  // AuditAssignment records (scheduled audits for a person/location)
    auditAssignmentHistory: any[]; // AuditAssignmentHistoryEntry records (status/assignee change log)
    // ── RBAC ("Фича Ролей") ───────────────────────────────────────────────────
    roles: any[];             // Role records (workspace-scoped)
    rolePermissions: any[];   // RolePermission records: {roleId, workspaceId, objectType, objectId}
    userRoles: any[];         // UserRole records: {userId, roleId, workspaceId} - who holds which role
}

function buildSeedDb(): MockDb {
    return {
        client: {...mockClient},
        clientUsers: allMockClientUsers.map(u => ({...u})),
        workspaces: [{...mockWorkspace}],
        items: [],
        wsUsers: [],
        taskListUnits: [],
        tasks: [],
        taskReports: [],
        locations: [],
        issues: [],
        auditTemplates: [],
        auditQuestions: [],
        auditRuns: [],
        auditAnswers: [],
        auditAssignments: [],
        auditAssignmentHistory: [],
        roles: mockRoles.map(r => ({...r})),
        rolePermissions: [],
        userRoles: mockUserRoles.map(r => ({...r})),
    };
}

function loadDb(): MockDb {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as MockDb;
            // Back-compat: fill in fields added after initial release.
            if (!parsed.items) parsed.items = [];
            if (!parsed.wsUsers) parsed.wsUsers = [];
            if (!parsed.taskListUnits) parsed.taskListUnits = [];
            if (!parsed.tasks) parsed.tasks = [];
            if (!parsed.taskReports) parsed.taskReports = [];
            if (!parsed.locations) parsed.locations = [];
            if (!parsed.issues) parsed.issues = [];
            if (!parsed.auditTemplates) parsed.auditTemplates = [];
            if (!parsed.auditQuestions) parsed.auditQuestions = [];
            if (!parsed.auditRuns) parsed.auditRuns = [];
            if (!parsed.auditAnswers) parsed.auditAnswers = [];
            if (!parsed.auditAssignments) parsed.auditAssignments = [];
            if (!parsed.auditAssignmentHistory) parsed.auditAssignmentHistory = [];
            if (!parsed.roles) parsed.roles = mockRoles.map(r => ({...r}));
            if (!parsed.rolePermissions) parsed.rolePermissions = [];
            if (!parsed.userRoles) parsed.userRoles = mockUserRoles.map(r => ({...r}));
            return parsed;
        }
    } catch (error) {
        console.warn('[mock-backend] failed to read mock db from localStorage, reseeding', error);
    }
    return buildSeedDb();
}

let db: MockDb = loadDb();

export function getMockDb(): MockDb {
    return db;
}

function persist() {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
    } catch (error) {
        console.warn('[mock-backend] failed to persist mock db to localStorage', error);
    }
}

export function updateMockDb(mutate: (db: MockDb) => void): void {
    mutate(db);
    persist();
}

export function resetMockDb(): void {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.warn('[mock-backend] failed to clear mock db from localStorage', error);
    }
    db = buildSeedDb();
}

export function reloadMockDb(): void {
    db = loadDb();
}
